/**
 * Slide-out chat panel — persistent across dashboard pages.
 *
 * Renders at the right edge of the viewport. Toggled via a floating button.
 */

import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, X, Plus, ChevronLeft } from 'lucide-react';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { useChatSession } from './useChatSession';

const PANEL_WIDTH = 420;

export default function ChatPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const { sessionId, messages, isStreaming, error, initSession, sendMessage, newSession } = useChatSession();
  const messagesEndRef = useRef(null);
  const [initialized, setInitialized] = useState(false);

  // Initialize session when panel opens
  useEffect(() => {
    if (isOpen && !initialized) {
      // Try to resume last session from localStorage
      const lastSession = localStorage.getItem('ci_chat_session');
      initSession(lastSession).then((sid) => {
        if (sid) localStorage.setItem('ci_chat_session', sid);
        setInitialized(true);
      });
    }
  }, [isOpen, initialized, initSession]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Save session ID
  useEffect(() => {
    if (sessionId) localStorage.setItem('ci_chat_session', sessionId);
  }, [sessionId]);

  const handleNewSession = async () => {
    const sid = await newSession();
    if (sid) localStorage.setItem('ci_chat_session', sid);
  };

  const showSuggestions = messages.length === 0;

  return (
    <>
      {/* Floating toggle button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#3EF1C7] text-black shadow-lg shadow-[#3EF1C7]/20 hover:shadow-[#3EF1C7]/40 hover:scale-105 transition-all flex items-center justify-center"
          title="Open CI Assistant"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 z-[60] h-full bg-[#0A0A0A] border-l border-white/10 shadow-2xl transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ width: PANEL_WIDTH }}
      >
        {/* Header — sticky, always visible */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#3EF1C7]/20 flex-shrink-0 bg-[#0d0d1a] sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#3EF1C7] animate-pulse" />
            <span className="font-semibold text-sm text-white">CI Assistant</span>
            <span className="text-xs text-white/30">
              {isStreaming ? 'thinking...' : 'online'}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleNewSession}
              className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
              title="New conversation"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-white/70 hover:text-red-400 transition-colors"
              title="Close chat"
              data-testid="chat-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages — scrollable area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-[#3EF1C7]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Commerce Index Assistant</h3>
              <p className="text-sm text-white/40 max-w-[280px] mx-auto">
                Ask about your business, check scores, manage deals, and get AI-powered recommendations.
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <ChatMessage key={i} message={msg} />
          ))}

          {/* Error display */}
          {error && (
            <div className="px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
              {error}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="absolute bottom-0 left-0 right-0">
          <ChatInput
            onSend={sendMessage}
            isStreaming={isStreaming}
            showSuggestions={showSuggestions}
          />
        </div>
      </div>
    </>
  );
}
